import { useState, useEffect, useRef } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CalendarDays, Clock, MapPin, User, Phone, Mail, CheckCircle, XCircle, Pause, Play, GripVertical } from "lucide-react";
import { format, isToday, isTomorrow, addDays, parseISO } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Booking } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";

interface CalendarViewProps {
  bookings: Booking[];
  onStatusUpdate: (bookingId: string, status: string) => void;
  onReschedule: (bookingId: string, newDate: Date, newTime: string) => void;
}

const statusColors = {
  pending: "bg-yellow-500",
  confirmed: "bg-blue-500", 
  in_progress: "bg-green-500",
  completed: "bg-gray-500",
  cancelled: "bg-red-500"
};

const statusIcons = {
  pending: Clock,
  confirmed: CheckCircle,
  in_progress: Play,
  completed: CheckCircle,
  cancelled: XCircle
};

export default function CalendarView({ bookings, onStatusUpdate, onReschedule }: CalendarViewProps) {
  const { t, tx } = useI18n();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [draggedBooking, setDraggedBooking] = useState<Booking | null>(null);
  const [dragOverDate, setDragOverDate] = useState<Date | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null);
  const [rescheduleTime, setRescheduleTime] = useState<string>("");
  const { toast } = useToast();
  const [capacityMap, setCapacityMap] = useState<Record<string, number>>({});
  const [quickMessage, setQuickMessage] = useState<string>("");
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);

  const getBookingsForDate = (date: Date) => {
    return bookings.filter(booking => {
      const bookingDate = new Date(booking.scheduled_date);
      return bookingDate.toDateString() === date.toDateString();
    });
  };

  // Load capacity for the month around selected date
  useEffect(() => {
    (async () => {
      try {
        const start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const end = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        const { data } = await (supabase as any)
          .from('vendor_availability')
          .select('date, capacity')
          .gte('date', start.toISOString().slice(0,10))
          .lte('date', end.toISOString().slice(0,10));
        const map: Record<string, number> = {};
        (data || []).forEach((row: any) => { map[row.date] = Number(row.capacity) || 0; });
        setCapacityMap(map);
      } catch {}
    })();
  }, [selectedDate]);

  const getCapacityForDate = (date: Date): number => {
    const key = format(date, 'yyyy-MM-dd');
    const cap = capacityMap[key];
    return (typeof cap === 'number' ? cap : 5); // default 5 if not set
  };

  const selectedDateBookings = getBookingsForDate(selectedDate);

  const handleStatusUpdate = (bookingId: string, newStatus: string) => {
    onStatusUpdate(bookingId, newStatus);
    toast({
      title: t('calendar.toast.status_updated'),
      description: tx('calendar.toast.status_changed', { status: newStatus.replace('_', ' ') }),
    });
  };

  const handleBookingClick = (booking: Booking) => {
    setSelectedBooking(booking);
    setQuickMessage(''); // Clear previous message
    setIsSheetOpen(true);
  };

  useEffect(() => {
    if (selectedBooking) {
      try {
        const d = new Date(selectedBooking.scheduled_date);
        if (!isNaN(d.getTime())) setRescheduleDate(d);
      } catch {}
      setRescheduleTime(
        selectedBooking.scheduled_time.includes(" - ")
          ? selectedBooking.scheduled_time.split(" - ")[0]
          : selectedBooking.scheduled_time
      );
    }
  }, [selectedBooking]);

  const handleDragStart = (e: React.DragEvent, booking: Booking) => {
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    setDragOverDate(date);
  };

  const handleDrop = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    if (draggedBooking) {
      const newDate = format(date, 'yyyy-MM-dd');
      onReschedule(draggedBooking.id, date, draggedBooking.scheduled_time);
      setDraggedBooking(null);
      setDragOverDate(null);
      toast({
        title: t('calendar.reschedule.title'),
        description: tx('calendar.toast.rescheduled', { date: format(date, 'MMM d, yyyy') }),
      });
    }
  };

  const handleDragEnd = () => {
    setDraggedBooking(null);
    setDragOverDate(null);
  };

  const getDateBadgeText = (date: Date) => {
    if (isToday(date)) return t('calendar.today');
    if (isTomorrow(date)) return t('calendar.tomorrow');
    return format(date, "MMM d");
  };

  const sendNotificationToCustomer = async (message: string) => {
    if (!selectedBooking) return;
    
    setIsSendingMessage(true);
    try {
      // Check if this is a guest booking (no customer_id)
      if (!selectedBooking.customer_id) {
        toast({
          title: 'Cannot send notification',
          description: 'This booking was made in guest mode. Notifications cannot be sent to guest bookings.',
          variant: 'destructive'
        });
        return;
      }

      // Debug: Log the booking data to see what fields are available
      console.log('Selected booking data:', selectedBooking);
      console.log('Customer ID:', selectedBooking.customer_id);

      // Build server payload; resolve profile/user on the server via service role
      const profileId = selectedBooking.customer_id || null;
      const email = (selectedBooking.customer_email || '').trim() || null;

      // Create notification via server API (uses service role to bypass RLS)
      const resp = await fetch('/api/notifications?action=push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Prefer profile_id; server can also resolve by email if provided
          profile_id: profileId || undefined,
          email: email || undefined,
          title: 'Message from your service provider',
          body: message,
          booking_id: selectedBooking.id,
          booking_number: selectedBooking.booking_number,
          link: '/profile'
        })
      });
      const result = await (async () => {
        try {
          return await resp.json();
        } catch {
          try {
            const txt = await resp.text();
            return { error: txt };
          } catch {
            return {};
          }
        }
      })();
      if (!resp.ok || (result && (result as any).error)) {
        // Fallback to direct RPC if server route is failing
        try {
          // Prefer server-side email resolver (security definer) to bypass RLS
          const emailForUser = (selectedBooking.customer_email || '').trim();
          const rpcRes = await (supabase as any).rpc('notify_user_by_email', {
            _email: emailForUser,
            _title: 'Message from your service provider',
            _body: message,
            _booking_id: selectedBooking.id,
            _link: '/profile',
            _booking_number: selectedBooking.booking_number
          });
          if (rpcRes?.error) {
            throw new Error(rpcRes.error.message);
          }
        } catch (e: any) {
          const msg =
            (result as any)?.details ||
            (result as any)?.error ||
            e?.message ||
            `HTTP ${resp.status}`;
          // If the user truly has no account, surface a clear message and stop
          if (/no_user_for_email|Cannot resolve user/i.test(String(msg))) {
            toast({
              title: 'Cannot send notification',
              description: 'Customer does not have an in-app account. Use "Invia via email" instead.',
              variant: 'destructive'
            });
            return;
          }
          throw new Error(msg);
        }
      }
      try { console.log('Notification push result:', result); } catch {}

      toast({
        title: 'Message sent',
        description: `The message has been sent to the customer and will appear in their notifications for booking ${selectedBooking.booking_number}.`,
        variant: 'default'
      });

      // Clear the message
      setQuickMessage('');

    } catch (error: any) {
      console.error('Error sending notification:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSendingMessage(false);
    }
  };

  return (
    <div className="space-y-6 w-full overflow-hidden">
      <Card className="border-gray-200 shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-900 break-words">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <CalendarDays className="h-5 w-5 text-primary" />
            </div>
            {t('calendar.title')}
          </CardTitle>
          <CardDescription className="text-gray-600 break-words">
            {t('calendar.desc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 min-w-0">
            {/* Calendar */}
            <div className="lg:col-span-2 min-w-0">
              <div className="overflow-x-auto">
                <div className="min-w-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="rounded-2xl border border-gray-200 shadow-sm w-full max-w-full"
                    modifiers={{
                      booked: (date) => getBookingsForDate(date).length > 0
                    }}
                    modifiersClassNames={{
                      booked: "bg-primary/10 font-semibold"
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Daily Schedule */}
            <div className="space-y-4 min-w-0">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">
                  {getDateBadgeText(selectedDate)}
                </h3>
                {(() => {
                  const count = selectedDateBookings.length;
                  const cap = getCapacityForDate(selectedDate);
                  const ratio = cap > 0 ? count / cap : (count > 0 ? 1.1 : 0);
                  const color = ratio < 0.7 ? 'bg-green-100 text-green-700' : ratio <= 1 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
                  return (
                    <Badge variant="secondary" className={color}>
                      {count}/{cap} {t('calendar.badge.bookings')}
                    </Badge>
                  );
                })()}
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto min-w-0">
                {selectedDateBookings.length === 0 ? (
                  <div className="text-center text-gray-500 p-8">
                    <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('calendar.empty')}</p>
                  </div>
                ) : (
                  selectedDateBookings
                    .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time))
                    .map((booking) => {
                      const StatusIcon = statusIcons[booking.status];
                      return (
                        <Card 
                          key={booking.id} 
                          className="cursor-pointer hover:shadow-medium transition-all duration-200 border-gray-200"
                          onClick={() => handleBookingClick(booking)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className={`w-3 h-3 rounded-full ${statusColors[booking.status]}`} />
                                  <span className="font-medium text-sm text-gray-700">
                                    {typeof booking.scheduled_time === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(booking.scheduled_time)
                                      ? booking.scheduled_time.slice(0,5)
                                      : booking.scheduled_time}
                                  </span>
                                </div>
                                <h4 className="font-medium text-gray-900">{booking.customer_name}</h4>
                                <p className="text-sm text-gray-600">{booking.service_name}</p>
                                <p className="text-sm font-semibold text-gray-900">{formatCurrency(booking.total_amount)}</p>
                              </div>
                              <StatusIcon className="h-4 w-4 text-gray-400" />
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Booking Details Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl max-h-[100vh] overflow-y-auto">
          {selectedBooking && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3 text-xl font-semibold text-gray-900 break-words">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  {t('calendar.details.title')}
                </SheetTitle>
                <SheetDescription className="text-gray-600 break-words">
                  {t('calendar.details.desc')}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* Customer Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold">{t('calendar.customer_info')}</h3>
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback>
                        {selectedBooking.customer_name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{selectedBooking.customer_name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-3 w-3" />
                        {selectedBooking.customer_email}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        {selectedBooking.customer_phone}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Service Details */}
                <div className="space-y-3">
                  <h3 className="font-semibold">{t('calendar.service_details')}</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">{t('calendar.field.service')}</span> {selectedBooking.service_name}</p>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      <span>{format(new Date(selectedBooking.scheduled_date), 'MMMM d, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{selectedBooking.scheduled_time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{selectedBooking.address}</span>
                    </div>
                    <p><span className="font-medium">{t('calendar.field.amount')}</span> {formatCurrency(selectedBooking.total_amount)}</p>
                    {selectedBooking.notes && (
                      <div>
                        <span className="font-medium">{t('calendar.field.notes')}</span>
                        <p className="text-sm text-muted-foreground mt-1">{selectedBooking.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Status Management */}
                <div className="space-y-4">
                  <h3 className="font-semibold">{t('calendar.status_mgmt')}</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{t('calendar.current_status')}</span>
                    <Badge 
                      variant={selectedBooking.status === 'completed' ? 'default' : 'secondary'}
                      className="capitalize"
                    >
                      {selectedBooking.status.replace('_', ' ')}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('calendar.update_status')}</label>
                    <Select 
                      value={selectedBooking.status}
                      onValueChange={(value) => handleStatusUpdate(selectedBooking.id, value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t('calendar.status.pending')}</SelectItem>
                        <SelectItem value="confirmed">{t('calendar.status.confirmed')}</SelectItem>
                        <SelectItem value="in_progress">{t('calendar.status.in_progress')}</SelectItem>
                        <SelectItem value="completed">{t('calendar.status.completed')}</SelectItem>
                        <SelectItem value="cancelled">{t('calendar.status.cancelled')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                {/* Reschedule */}
                <div className="space-y-4">
                  <h3 className="font-semibold">{t('calendar.reschedule.title')}</h3>
                  <div className="space-y-6">
                    {((selectedBooking as any)?.window_start_at || (selectedBooking as any)?.window_end_at) && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <div className="font-medium">Finestra consentita (bulk)</div>
                        <div className="text-xs text-amber-800 mt-1 break-words">
                          {(() => {
                            const ws = (selectedBooking as any)?.window_start_at ? new Date((selectedBooking as any).window_start_at) : null;
                            const we = (selectedBooking as any)?.window_end_at ? new Date((selectedBooking as any).window_end_at) : null;
                            const fmt = (d: Date | null) => (d && !isNaN(d.getTime()) ? d.toLocaleString() : "—");
                            return `Da ${fmt(ws)} a ${fmt(we)}`;
                          })()}
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">{t('calendar.reschedule.new_date')}</label>
                      <div className="flex justify-center overflow-x-auto">
                        <div className="w-full max-w-full">
                          <Calendar
                            mode="single"
                            selected={rescheduleDate || undefined}
                            onSelect={(d) => setRescheduleDate(d || null)}
                            className="rounded-md border w-full max-w-full"
                            showOutsideDays={false}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 min-w-0">
                      <label className="text-sm font-medium">{t('calendar.reschedule.start_time')}</label>
                      <Input
                        type="time"
                        value={rescheduleTime}
                        onChange={(e) => setRescheduleTime(e.target.value)}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">{t('calendar.reschedule.helper')}</p>
                    </div>
                  </div>
                  <div>
                    <Button
                      onClick={() => {
                        if (!rescheduleDate || !rescheduleTime) {
                          toast({ title: t('common.error'), description: t('calendar.toast.missing_fields'), variant: "destructive" });
                          return;
                        }
                        // Enforce window for bulk bookings (if present)
                        try {
                          const wsRaw = (selectedBooking as any)?.window_start_at;
                          const weRaw = (selectedBooking as any)?.window_end_at;
                          if (wsRaw && weRaw) {
                            const ws = new Date(wsRaw);
                            const we = new Date(weRaw);
                            const [hh, mm] = String(rescheduleTime).split(":").map((x) => Number(x || 0));
                            const proposed = new Date(rescheduleDate);
                            proposed.setHours(hh || 0, mm || 0, 0, 0);
                            if (!isNaN(ws.getTime()) && !isNaN(we.getTime()) && (proposed < ws || proposed > we)) {
                              toast({
                                title: "Orario fuori finestra",
                                description: "Seleziona un orario compreso tra checkout e check-in (meno buffer).",
                                variant: "destructive",
                              });
                              return;
                            }
                          }
                        } catch {}
                        onReschedule(selectedBooking.id, rescheduleDate, rescheduleTime);
                      }}
                    >
                      {t('calendar.reschedule.save')}
                    </Button>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-3">
                  <h3 className="font-semibold">{t('calendar.quick_actions.title')}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`tel:${selectedBooking.customer_phone}`, '_self');
                      }}
                    >
                      <Phone className="h-4 w-4 mr-1" />
                      {t('calendar.quick_actions.call')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`mailto:${selectedBooking.customer_email}`, '_blank');
                      }}
                    >
                      <Mail className="h-4 w-4 mr-1" />
                      {t('calendar.quick_actions.email')}
                    </Button>
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <div className="text-sm font-medium">{t('calendar.quick_actions.title')}</div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        t('calendar.quick_msgs.on_way'),
                        t('calendar.quick_msgs.late'),
                        t('calendar.quick_msgs.access'),
                        t('calendar.quick_msgs.parking'),
                      ].map((msg) => (
                        <Button 
                          key={msg} 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setQuickMessage(msg)}
                        >
                          {msg}
                        </Button>
                      ))}
                    </div>
                    <Textarea 
                      placeholder={t('calendar.quick_msgs.placeholder')}
                      value={quickMessage}
                      onChange={(e) => setQuickMessage(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => sendNotificationToCustomer(quickMessage)}
                        disabled={!quickMessage.trim() || isSendingMessage}
                      >
                        {isSendingMessage ? 'Sending...' : t('calendar.quick_msgs.copy_send')}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => window.open(`mailto:${selectedBooking.customer_email}`, '_blank')}
                      >
                        {t('calendar.quick_msgs.send_email')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}